import type {
  AgedReceivablesResponseDto,
  AgingResponseDto,
  BasSummaryResponseDto,
  ClientReportResponseDto,
  CreateReceiptBundleJobDto,
  CreateReceiptBundleJobResponseDto,
  ExpenseReportResponseDto,
  ExportJobStatusResponseDto,
  InvoiceSummaryResponseDto,
  LineItemReportResponseDto,
  ProfitAndLossResponseDto,
  ReceiptBundleEstimateResponseDto,
  ReportPackControllerGetReceiptBundleEstimateParams,
  ReportsControllerGetAgedReceivablesParams,
  ReportsControllerGetAgingParams,
  ReportsControllerGetBasSummaryParams,
  ReportsControllerGetClientReportParams,
  ReportsControllerGetExpenseReportParams,
  ReportsControllerGetInvoiceSummaryParams,
  ReportsControllerGetLineItemReportParams,
  ReportsControllerGetProfitAndLossParams,
  ReportsControllerGetRevenueByTagParams,
  ReportsControllerGetRevenueTimelineParams,
  ReportsControllerGetTaxReportParams,
  RevenueTimelineResponseDto,
  TagRevenueResponseDto,
  TaxReportResponseDto,
  TurnoverResponseDto,
} from '@trellis/api'
import type { ReportKey, RequestResult } from '@trellis/shared'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { downloadAuthenticatedFile } from '@/lib/download-file'
import { request } from '@/lib/request'

export type ExpenseReportParams = ReportsControllerGetExpenseReportParams
export type ExpenseReportResponse = ExpenseReportResponseDto

export type InvoiceSummaryParams = ReportsControllerGetInvoiceSummaryParams
export type ClientReportParams = ReportsControllerGetClientReportParams
export type LineItemReportParams = ReportsControllerGetLineItemReportParams
export type RevenueTimelineParams = ReportsControllerGetRevenueTimelineParams
export type AgingParams = ReportsControllerGetAgingParams
export type RevenueByTagParams = ReportsControllerGetRevenueByTagParams

export const getInvoiceSummary = (params: InvoiceSummaryParams = {}) =>
  request<InvoiceSummaryResponseDto>(() =>
    apiClient.api.reportsControllerGetInvoiceSummary(params),
  )

export const getClientReport = (params: ClientReportParams = {}) =>
  request<ClientReportResponseDto>(() =>
    apiClient.api.reportsControllerGetClientReport(params),
  )

export const getLineItemReport = (params: LineItemReportParams = {}) =>
  request<LineItemReportResponseDto>(() =>
    apiClient.api.reportsControllerGetLineItemReport(params),
  )

export const getRevenueTimeline = (params: RevenueTimelineParams = {}) =>
  request<RevenueTimelineResponseDto>(() =>
    apiClient.api.reportsControllerGetRevenueTimeline(params),
  )

export const getAging = (params: AgingParams = {}) =>
  request<AgingResponseDto>(() => apiClient.api.reportsControllerGetAging(params))

export const getRevenueByTag = (params: RevenueByTagParams = {}) =>
  request<TagRevenueResponseDto>(() =>
    apiClient.api.reportsControllerGetRevenueByTag(params),
  )

export const getExpenseReport = (params: ExpenseReportParams) =>
  request<ExpenseReportResponse>(() =>
    apiClient.api.reportsControllerGetExpenseReport(params),
  )

export type TaxReportParams = ReportsControllerGetTaxReportParams

export const getTaxReport = (params: TaxReportParams) =>
  request<TaxReportResponseDto>(() => apiClient.api.reportsControllerGetTaxReport(params))

export type BasSummaryParams = ReportsControllerGetBasSummaryParams
export type ProfitAndLossParams = ReportsControllerGetProfitAndLossParams
export type AgedReceivablesParams = ReportsControllerGetAgedReceivablesParams

export const getBasSummary = (params: BasSummaryParams) =>
  request<BasSummaryResponseDto>(() =>
    apiClient.api.reportsControllerGetBasSummary(params),
  )

export const getProfitAndLoss = (params: ProfitAndLossParams) =>
  request<ProfitAndLossResponseDto>(() =>
    apiClient.api.reportsControllerGetProfitAndLoss(params),
  )

export const getAgedReceivables = (params: AgedReceivablesParams = {}) =>
  request<AgedReceivablesResponseDto>(() =>
    apiClient.api.reportsControllerGetAgedReceivables(params),
  )

export const getTurnover = () =>
  request<TurnoverResponseDto>(() => apiClient.api.reportsControllerGetTurnover())

export type ReportExportFormat = 'csv' | 'pdf'
export type ReportExportParams = Record<string, string | string[] | undefined>

export interface ExportReportOptions {
  reportKey: ReportKey
  format: ReportExportFormat
  params?: ReportExportParams
}

/**
 * Mirrors ReportExportService#buildFileName on the backend so the on-screen filename
 * never drifts from what the server would have named the same request.
 */
function buildExportFileName(
  reportKey: ReportKey,
  format: ReportExportFormat,
  params: ReportExportParams,
): string {
  const { startDate, endDate } = params
  const datePart =
    typeof startDate === 'string' && typeof endDate === 'string'
      ? `${startDate}-${endDate}`
      : new Date().toISOString().split('T')[0]
  return `${reportKey}-${datePart}.${format}`
}

/**
 * The export endpoint streams a raw CSV/PDF body rather than the ApiSuccess envelope, so
 * it bypasses apiClient/request() entirely — same pattern as data-export.requests.ts.
 */
export async function exportReport({
  reportKey,
  format,
  params = {},
}: ExportReportOptions): Promise<void> {
  const url = new URL(`${env.VITE_API_BASE_URL}/api/reports/export/${reportKey}`)
  url.searchParams.set('format', format)
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      for (const item of value) url.searchParams.append(key, item)
    } else {
      url.searchParams.set(key, value)
    }
  }

  await downloadAuthenticatedFile(
    url.toString(),
    buildExportFileName(reportKey, format, params),
  )
}

/**
 * Zip stream (P&L PDF, BAS summary PDF, invoices/expenses/payments CSV), so it bypasses
 * apiClient/request() entirely — same pattern as {@link exportReport}.
 */
export async function exportEofyPack(financialYear: string): Promise<void> {
  const url = new URL(`${env.VITE_API_BASE_URL}/api/reports/packs/eofy`)
  url.searchParams.set('financialYear', financialYear)

  await downloadAuthenticatedFile(url.toString(), `eofy-pack-${financialYear}.zip`)
}

/**
 * Starts a background job producing one archive with both the EOFY pack's documents and
 * every receipt for the period — the "include receipts" checkbox turns the EOFY pack from
 * a synchronous {@link exportEofyPack} download into this async job.
 *
 * `includeReceipts=true` always returns the JSON job envelope (never the zip
 * {@link exportEofyPack} streams), so this can go through apiClient/request() like the
 * rest of this file. The generated `reportPackControllerGetEofyPack` is untyped (`void,
 * void`) because swagger-typescript-api only derives a response type from the endpoint's
 * 200 response, and the 200 here is the zip case's `@ApiOkResponse` with no `type` — the
 * 202 JSON envelope's type is documented separately via `@ApiAcceptedResponse` but isn't
 * picked up. request<T>() below supplies the real type; this is safe because apiClient
 * defaults to `format: 'json'`, so the response is actually parsed as JSON at runtime
 * regardless of the generated method's `void` typing.
 */
export function createEofyPackWithReceiptsJob(
  financialYear: string,
): Promise<RequestResult<CreateReceiptBundleJobResponseDto>> {
  return request<CreateReceiptBundleJobResponseDto>(() =>
    apiClient.api.reportPackControllerGetEofyPack({
      financialYear,
      includeReceipts: true,
    }),
  )
}

export type ReceiptBundleDateRange = ReportPackControllerGetReceiptBundleEstimateParams

/** Cheap pre-commit size estimate for the frontend's receipt bundle date range picker. */
export const getReceiptBundleEstimate = (params: ReceiptBundleDateRange) =>
  request<ReceiptBundleEstimateResponseDto>(() =>
    apiClient.api.reportPackControllerGetReceiptBundleEstimate(params),
  )

export const createReceiptBundleJob = (body: CreateReceiptBundleJobDto) =>
  request<CreateReceiptBundleJobResponseDto>(() =>
    apiClient.api.reportPackControllerCreateReceiptBundleJob(body),
  )

export const getReceiptBundleJob = (jobId: string) =>
  request<ExportJobStatusResponseDto>(() =>
    apiClient.api.reportPackControllerGetReceiptBundleJob({ jobId }),
  )
